/**
 * Централизованное управление всеми React Query ключами в приложении
 * 
 * Преимущества централизации:
 * - Избежание опечаток в строковых ключах
 * - Упрощение рефакторинга
 * - Консистентность кэширования
 * - Единая точка истины для всех query keys
 * 
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */

/**
 * Query keys для пользователей (user entity)
 */
export const userQueryKeys = {
  /** Текущий авторизованный пользователь */
  currentUser: () => ['currentUser'] as const,
  
  /** Профиль конкретного пользователя */
  userProfile: (userId: string | null) => ['userProfile', userId] as const,
  
  /** Поиск пользователей */
  search: (query: string) => ['user-search', query] as const,
  
  /** Все профили (для инвалидации счетчиков и т.д.) */
  profiles: () => ['profiles'] as const,
  
  /** Дефолтная видимость вишлистов */
  defaultWishlistVisibility: (userId: string) => ['default-wishlist-visibility', userId] as const,
} as const;

/**
 * Query keys для друзей (friend entity)
 */
export const friendQueryKeys = {
  /** Все друзья пользователя (с пагинацией) */
  friends: (userId?: string) => ['friends', userId] as const,
  
  /** Количество друзей пользователя */
  friendsCount: (userId?: string) => ['friends-count', userId] as const,
  
  /** Входящие запросы в друзья */
  incomingRequests: (userId?: string) => ['incoming-friend-requests', userId] as const,
  
  /** Исходящие запросы в друзья */
  outgoingRequests: (userId?: string) => ['outgoing-friend-requests', userId] as const,
  
  /** Статус дружбы с конкретным пользователем */
  friendshipStatus: (targetUserId: string) => ['friendship-status', targetUserId] as const,
  
  /** Все ключи связанные с друзьями (для инвалидации) */
  all: () => ['friends'] as const,

  /** Все входящие запросы (для инвалидации) */
  allIncomingRequests: () => ['incoming-friend-requests'] as const,

  /** Все исходящие запросы (для инвалидации) */
  allOutgoingRequests: () => ['outgoing-friend-requests'] as const,

  /** Все статусы дружбы (для инвалидации) */
  allFriendshipStatuses: () => ['friendship-status'] as const,

  /** Все счётчики друзей (для инвалидации) */
  allFriendsCount: () => ['friends-count'] as const,
} as const;

/**
 * Query keys для вишлистов (wishlist entity)
 */
export const wishlistQueryKeys = {
  /** Все вишлисты пользователя */
  wishlists: (userId?: string, showOnlyPublic?: boolean) => ['wishlists', userId, showOnlyPublic] as const,
  
  /** Конкретный вишлист */
  wishlist: (wishlistId: string) => ['wishlist', wishlistId] as const,
  
  /** Желание из вишлиста */
  wishlistItem: (wishlistId: string, itemId: string) => ['wishlist-item', wishlistId, itemId] as const,
  
  /** Желание вместе с данными вишлиста (по itemId) */
  wishlistItemWithWishlist: (itemId: string) => ['wishlist-item-with-wishlist', itemId] as const,
  
  /** Желание по ID (без wishlistId) */
  wishlistItemById: (itemId: string) => ['wishlist-item', itemId] as const,
  
  /** Публичные вишлисты (лента) */
  publicWishlists: (sortBy?: string, currentUserId?: string) => ['public-wishlists', sortBy, currentUserId] as const,
  
  /** Статистика пользователя */
  userStats: (userId?: string) => ['userStats', userId] as const,
  
  /** Избранные вишлисты */
  favoriteWishlists: (userId?: string) => ['favorite-wishlists', userId] as const,
  
  /** Проверка - является ли вишлист избранным */
  isFavorite: (wishlistId: string, userId?: string) => ['is-favorite', wishlistId, userId] as const,
  
  /** Соавторы вишлиста */
  collaborators: (wishlistId: string | null) => ['wishlist-collaborators', wishlistId] as const,
  
  /** Ожидающие приглашения в соавторы */
  pendingInvitations: (userId: string | undefined) => ['pending-collaborator-invitations', userId] as const,
  
  /** Все ожидающие приглашения (для инвалидации, prefix match) */
  allPendingInvitations: () => ['pending-collaborator-invitations'] as const,
  
  /** Популярные желания */
  popularItems: (sortBy?: string, currentUserId?: string) => ['popularItems', sortBy, currentUserId] as const,
  
  /** Популярные желания (infinite scroll с серверными фильтрами) */
  popularItemsInfinite: (
    sortBy?: string,
    currentUserId?: string,
    search?: string,
    category?: string | null,
    priceMin?: number,
    priceMax?: number,
    onlyWithPrice?: boolean,
    onlyWithLink?: boolean,
  ) => ['popularItems', 'infinite', sortBy, currentUserId, search, category, priceMin, priceMax, onlyWithPrice, onlyWithLink] as const,
  
  /** Все ключи связанные с популярными желаниями (для инвалидации) */
  allPopularItems: () => ['popularItems'] as const,
  
  /** Все желания пользователя */
  userItems: (userId: string | undefined, limit?: number) => ['user-items', userId, limit] as const,
  
  /** Все ключи связанные с вишлистами (для инвалидации) */
  all: () => ['wishlist'] as const,
  
  /** Все ключи связанные со списком вишлистов (для инвалидации) */
  allLists: () => ['wishlists'] as const,
  
  /** Все ключи связанные с желаниями (для инвалидации) */
  allItems: () => ['wishlist-item'] as const,

  /** Все желания пользователей (для инвалидации) */
  allUserItems: () => ['user-items'] as const,

  /** Вся статистика пользователей (для инвалидации) */
  allUserStats: () => ['userStats'] as const,

  /** Все публичные вишлисты (для инвалидации) */
  allPublicWishlists: () => ['public-wishlists'] as const,

  /** Забронированные подарки пользователя */
  bookedItems: (userId?: string) => ['my-booked-items', userId] as const,

  /** Все забронированные подарки (для инвалидации) */
  allBookedItems: () => ['my-booked-items'] as const,
} as const;

/**
 * Query keys для Тайного Санты (secret-santa entity)
 */
export const secretSantaQueryKeys = {
  /** Все игры Тайный Санта пользователя */
  games: (userId?: string) => ['secret-santa-games', userId] as const,
  
  /** Активные игры Тайный Санта */
  activeGames: () => ['secret-santa-games', 'active'] as const,
  
  /** Конкретная игра */
  game: (gameId: string | undefined) => ['secret-santa-game', gameId] as const,
  
  /** Назначение (кому дарить) в игре */
  assignment: (gameId: string | undefined) => ['secret-santa-assignment', gameId] as const,
  
  /** Все ключи связанные с Тайным Сантой (для инвалидации) */
  all: () => ['secret-santa-games'] as const,

  /** Все назначения (для инвалидации) */
  allAssignments: () => ['secret-santa-assignment'] as const,
} as const;

/**
 * Query keys для уведомлений (notification entity)
 */
export const notificationQueryKeys = {
  /** Уведомления пользователя */
  notifications: (userId: string | undefined) => ['notifications', userId] as const,

  /** Количество непрочитанных уведомлений */
  unreadCount: (userId: string) => ['unread-notifications-count', userId] as const,
} as const;

/**
 * Query keys для ленты (feed entity)
 */
export const feedQueryKeys = {
  /** Лента активностей пользователя */
  feed: (userId?: string) => ['feed', userId] as const,
} as const;

/**
 * Query keys для блокировки пользователей (blocked-user entity)
 */
export const blockedUserQueryKeys = {
  /** Список заблокированных пользователей */
  list: () => ['blocked-users'] as const,

  /** Проверка блокировки конкретного пользователя */
  isBlocked: (targetUserId: string) => ['is-blocked', targetUserId] as const,

  /** Все ключи связанные с блокировками (для инвалидации) */
  all: () => ['blocked-users'] as const,

  /** Все проверки блокировки (для инвалидации) */
  allIsBlocked: () => ['is-blocked'] as const,
} as const;

/**
 * Query keys для подборок (selection entity)
 */
export const selectionQueryKeys = {
  /** Список подборок */
  list: () => ['selections'] as const,

  /** Детальная информация о подборке */
  detail: (selectionId: string | undefined) => ['selection-detail', selectionId] as const,

  /** Все ключи связанные с подборками (для инвалидации) */
  all: () => ['selections'] as const,
} as const;